import { Annotation, AnnotationRect } from '@readcloud/data';
import {
  annotationsActions,
  authSelectors,
  cloudSelectors,
  pdfSelectors,
  readerSelectors,
  useAppDispatch,
  usersSelectors,
} from '@readcloud/state';
import { ObjectId } from 'bson';
import React from 'react';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { TextSelectionContext } from '../../context/TextSelection';
import { Section } from '../../types/epubs';
import { isAnnotationShared } from '../../utils/annotationUtils';
import { SelectionState } from '../CustomSelection/SelectionStream';
import { AnnotationModal } from './AnnotationModal/AnnotationModal';

export type AddAnnotationProps = {
  onCancel: () => void;
  onSuccess: () => void;
  mode: 'Add';
  selectionState: SelectionState;
  section: Section;
} & Pick<
  React.ComponentProps<typeof AnnotationModal>,
  'ctaText' | 'initialValues' | 'noColor' | 'noteRequired' | 'open'
>;

export type UpdateAnnotationProps = {
  onCancel: () => void;
  onSuccess: () => void;
  mode: 'Update';
  previousAnnotation: Annotation;
  selectionState: SelectionState;
} & Pick<
  React.ComponentProps<typeof AnnotationModal>,
  'ctaText' | 'initialValues' | 'noColor' | 'noteRequired' | 'open'
>;
export const ConnectedAnnotationModal = (
  props: AddAnnotationProps | UpdateAnnotationProps
) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const user = useSelector(authSelectors.getUser);
  const knownUsers = useSelector(usersSelectors.getKnownUsers);
  const knownClouds = useSelector(cloudSelectors.getKnownClouds);
  const pdfData = useSelector(pdfSelectors.getPDFData);
  const dispatch = useAppDispatch();

  const { type, location } = React.useContext(DocumentWithExtrasContext);

  const [initialSelection, setInitialSelection] =
    React.useState<SelectionState>(undefined);

  React.useEffect(() => {
    if (!!props.selectionState) {
      setInitialSelection(props.selectionState);
    }
  }, [props.selectionState?.selectedText]);

  return readerData.cloudId && user && readerData.bookId ? (
    <AnnotationModal
      noteRequired={props.noteRequired}
      noColor={props.noColor}
      cloudId={readerData.cloudId}
      knownClouds={knownClouds}
      knownUsers={knownUsers}
      ctaText={props.ctaText}
      open={props.open}
      initialValues={props.initialValues}
      onClose={props.onCancel}
      onSuccess={({ visibleInClouds, visibleToUsers, note, shared, color }) => {
        if (!user || !readerData.bookId || !readerData.cloudId) {
          return;
        }
        if (props.mode === 'Update') {
          const newAnno = {
            ...props.previousAnnotation,
            updated: new Date().toISOString(),
            clientUpdated: new Date().toISOString(),
            color,
            text: note,
            ...(shared
              ? {
                  visibleInClouds,
                  visibleToUsers,
                }
              : {
                  visibleInClouds: [],
                  visibleToUsers: [],
                }),
          };
          dispatch(annotationsActions.asyncActions.updateAnnotation(newAnno));
        } else if (props.mode === 'Add') {
          let newAnnotation = undefined;
          if (type === 'EPUB') {
            newAnnotation = {
              created: new Date().toISOString(),
              clientCreated: new Date().toISOString(),
              id: new ObjectId().valueOf().toString(),
              author: user.id,
              book: readerData.bookId,
              selectedText: initialSelection?.selectedText,
              type: 'Text',
              tags: [],
              visibleInClouds: [],
              visibleToUsers: [],
              location: {
                pageNumber: location,
                range: props.section?.cfiFromRange(
                  initialSelection?.nativeRange
                ),
              },
              bookType: 'EPUB' as const,
              deleted: false,
              color,
              ...{ text: note },
              ...(shared
                ? {
                    visibleInClouds,
                    visibleToUsers,
                  }
                : {}),
              version: '2',
              updated: new Date().toISOString(),
              clientUpdated: new Date().toISOString(),
            };
          } else if (type === 'PDF') {
            //for some reason the rects array from Range.getClientRects() produces multiple rects covering the same text, with slightly different x/y coords
            //This removes any rects that are within 5px x/y of eachother, except for one (the first one found).
            //if there is a problem with annotation rects missing, this is most likely the cause.
            const removeDuplicateRects = (rects: AnnotationRect[]) => {
              const tempRects = [];
              for (let i = 0; i < rects.length; ++i) {
                if (
                  !tempRects.find(
                    (tempRect) =>
                      Math.abs(rects[i].x - tempRect.x) < 5 &&
                      Math.abs(rects[i].y - tempRect.y) < 5
                  )
                ) {
                  tempRects.push(rects[i]);
                }
              }

              return tempRects;
            };

            const { top: offsetTop, left: offsetLeft } =
              initialSelection?.referenceElement.getBoundingClientRect();

            newAnnotation = {
              created: new Date().toISOString(),
              clientCreated: new Date().toISOString(),
              id: new ObjectId().valueOf().toString(),
              author: user.id,
              book: readerData.bookId,
              selectedText: initialSelection?.selectedText,
              type: 'Text',
              tags: [],
              visibleInClouds: [],
              visibleToUsers: [],
              location: {
                pageNumber: parseInt(initialSelection?.location),
              },
              data: {
                // these calculations are to get the position of the rect based on the top left corner of the parent text layer, when at a scale of 1.
                rects: removeDuplicateRects(
                  Array.from(
                    initialSelection?.nativeRange?.getClientRects()
                  ).map((rect) => ({
                    x: (rect.x - offsetLeft) / pdfData.scale,
                    y: (rect.y - offsetTop) / pdfData.scale,
                    top: (rect.top - offsetTop) / pdfData.scale,
                    left: (rect.left - offsetLeft) / pdfData.scale,
                    right: (rect.right - offsetLeft) / pdfData.scale,
                    bottom: (rect.bottom - offsetTop) / pdfData.scale,
                    width: rect.width / pdfData.scale,
                    height: rect.height / pdfData.scale,
                  })) as DOMRect[]
                ),
                scaleWithPage: true,
              },
              bookType: 'PDF' as const,
              deleted: false,
              color,
              ...{ text: note },
              ...(shared
                ? {
                    visibleInClouds,
                    visibleToUsers,
                  }
                : {}),
              version: '2',
              updated: new Date().toISOString(),
              clientUpdated: new Date().toISOString(),
            };
          } else if (type === 'PLAYER') {
            newAnnotation = {
              created: new Date().toISOString(),
              clientCreated: new Date().toISOString(),
              id: new ObjectId().valueOf().toString(),
              author: user.id,
              book: readerData.bookId,
              selectedText: initialSelection?.selectedText || '',
              type: 'Text',
              tags: [],
              visibleInClouds: [],
              visibleToUsers: [],
              location: {
                pageNumber: parseInt(initialSelection?.location) || '1',
              },
              bookType: 'MEDIA' as const,
              deleted: false,
              color,
              ...{ text: note },
              ...(shared
                ? {
                    visibleInClouds,
                    visibleToUsers,
                  }
                : {}),
              version: '2',
              updated: new Date().toISOString(),
              clientUpdated: new Date().toISOString(),
            };
          }

          if (newAnnotation) {
            dispatch(
              annotationsActions.asyncActions.addAnnotation({
                ...newAnnotation,
                version: '2',
              })
            );
          }
        }
        props.onSuccess();
      }}
    />
  ) : null;
};

export const ConnectedAnnotationAddModal = (
  props: Omit<AddAnnotationProps, 'ctaText' | 'initialValues' | 'mode'>
) => {
  const { selectionState } = React.useContext(TextSelectionContext);
  return (
    <ConnectedAnnotationModal
      {...props}
      mode="Add"
      ctaText="Add"
      initialValues={{
        annotatedText: selectionState?.selectedText,
      }}
    />
  );
};

export const ConnectedAnnotationUpdateModal = (
  props: Omit<UpdateAnnotationProps, 'ctaText' | 'initialValues' | 'mode'>
) => {
  return (
    <ConnectedAnnotationModal
      {...props}
      mode="Update"
      ctaText="Update"
      initialValues={{
        note:
          props.previousAnnotation?.type === 'Text'
            ? props.previousAnnotation.text || undefined
            : undefined,
        annotatedText:
          props.previousAnnotation && 'selectedText' in props.previousAnnotation
            ? props.previousAnnotation.selectedText
            : undefined,
        color: props.previousAnnotation?.color || undefined,
        shared: isAnnotationShared(props.previousAnnotation),
        visibleToUsers: props.previousAnnotation?.visibleToUsers,
        visibleInClouds: props.previousAnnotation?.visibleInClouds,
      }}
    />
  );
};
